// "Conversations" - the per-project tab where an operator holds a
// conversation with the Manager. The UI calls this fleet "Agents"; every
// identifier stays `stateful*`.
//
// There is no accept step: a state edit commits at the end of every turn and a
// rule change applies in the conversation that proposes it.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAgentSlot } from "../api/agentSlot";

import {
  useStartStatefulRun,
  useStatefulRun,
  useStatefulRunTurn,
  useStatefulAgents,
  useStatefulRuns,
  type StatefulRun,
} from "../api/hooks/useStatefulAgents";
import { useCancelOperation } from "../api/hooks/useAsyncOperation";
import { ViewAiTraceButton } from "../api";
import { agentDisplayLabel } from "../utils/runParticipants";
import { formatWhen } from "../utils/formatWhen";
import { ConversationStarter } from "../components/ConversationStarter";
import { RunsTable } from "../components/RunsTable";
import { TypeFilter } from "../components/ConversationFilters";
import { DateRangePicker, lastDays } from "../components/DateRangePicker";
import { RunDriveDocuments, RunGeneratedFiles, RunSpendText } from "../components/RunView";
import { LiveTurnPanel } from "../components/RunLivePanels";
import { RunTranscript } from "../components/RunTranscript";
import { RunTitleBar } from "../components/RunTitleBar";
import { agentLabelFrom, runParticipantsLabel, runStarterLabel } from "../utils/runParticipants";
import {
  projectConversationPath,
  projectConversationsPath,
  projectScheduledPath,
} from "./learningAgentPaths";
import { RunComposer } from "../components/RunComposer";
import { useComposerQuote } from "../components/useComposerQuote";
import { ScrollToLatest } from "../components/ScrollToLatest";
import { leftAfterSend } from "../utils/leftAfterSend";
import { useOpenAtLatest } from "../utils/scrollToLatest";
import { useAttachments } from "../api/hooks/useAttachments";
import styles from "./ProjectStatefulRunsTab.module.css";

// Matches the Scheduled tab and the specialist's own lists.
const RUNS_DEFAULT_DAYS = 30;

const runsHref = projectConversationsPath;
const runHref = projectConversationPath;

const RunNotFound = ({ runId, backHref }: { runId: string; backHref: string }) => (
  <section className={styles.detail}>
    <p className={styles.error}>
      Run <code>{runId}</code> was not found on this project.
    </p>
    <Link className={styles.link} to={backHref}>
      Back to runs
    </Link>
  </section>
);

// A read that FAILED is not a run that does not exist. Rendering "not found" for
// a 5xx / transport error tells the operator their run is gone and, because the
// poll only runs while there is data, leaves the route dead - hence an explicit
// retry rather than a dead end.
const RunLoadFailed = ({
  runId,
  detail,
  backHref,
  onRetry,
  retrying,
}: {
  runId: string;
  detail: string;
  backHref: string;
  onRetry: () => void;
  retrying: boolean;
}) => (
  <section className={styles.detail}>
    <p className={styles.error}>
      Could not load run <code>{runId}</code>. {detail}
    </p>
    <div className={styles.composerRow}>
      <button type="button" className={styles.secondary} disabled={retrying} onClick={onRetry}>
        {retrying ? "Retrying…" : "Try again"}
      </button>
      <Link className={styles.link} to={backHref}>
        Back to runs
      </Link>
    </div>
  </section>
);

const RunDetailPanel = ({
  runId,
  projectId,
  actorEmail,
}: {
  runId: string;
  projectId: string;
  actorEmail: string;
}) => {
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [stopRequested, setStopRequested] = useState(false);
  const detailQuery = useStatefulRun(runId);
  const detail = detailQuery.data;
  // Liveness is the operation row's, not the run's pointer: a worker that died
  // never cleared the pointer, and the row's read-time self-heal is what turns
  // that into an honest "nothing is running" (principle 16).
  const liveTurn = detail?.turn ?? null;
  // Who is answering this conversation. A run started with a specialist answers as
  // that specialist; only an unnamed one is fronted by the Manager.
  const answeringAgent = agentDisplayLabel(detail?.run.agent || "manager");
  const inFlight = liveTurn !== null;
  const ended = detail?.run.status === "ended";
  const stopping = stopRequested || Boolean(liveTurn?.cancel_requested);
  const backHref = runsHref(projectId);
  const navigate = useNavigate();

  const turn = useStatefulRunTurn(runId, projectId);
  const cancelOp = useCancelOperation();
  const attachments = useAttachments();
  const contentRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const quote = useComposerQuote(setMessage, composerRef);
  useOpenAtLatest({ contentRef, barRef, conversationId: runId, ready: !!detail });

  // Named rather than inlined on the button, so the send shortcut runs exactly the same
  // path. The guards are repeated here rather than left to `disabled`: the
  // keyboard never consults a disabled button.
  const send = () => {
    // While a turn runs the message is queued behind it.
    if (!message.trim() || turn.isLoading) return;
    if (attachments.uploading || attachments.failed) return;
    const sent = attachments.ready;
    const content = message.trim();
    const atSend = message;
    setError(null);
    turn.mutate(
      { content, actor_email: actorEmail, ...sent },
      {
        onSuccess: (launched) => {
          setMessage((m) => leftAfterSend(m, atSend));
          attachments.release(sent);
          if (launched.status !== "queued") setStopRequested(false);
        },
        onError: (e) => setError(e.message),
      },
    );
  };

  const onStop = async () => {
    if (!liveTurn || stopRequested) return;
    setError(null);
    // Optimistic; the poll's cancel_requested is what keeps it durable across a
    // reload while the agent winds down to a graceful summary.
    setStopRequested(true);
    try {
      await cancelOp.mutateAsync({ operation_key: liveTurn.operation_key });
    } catch (e) {
      // Roll back so the operator can retry - the turn is still running.
      setStopRequested(false);
      setError(e instanceof Error ? e.message : "Could not stop the manager.");
    }
  };

  if (detailQuery.isLoading) {
    return <p className={styles.muted}>Loading run…</p>;
  }
  // A failed REFETCH keeps `data` and sets `error` side by side, and this query
  // polls every 2s while a turn runs - so an error is only allowed to take over
  // the screen when we hold no conversation at all. Otherwise one 5xx would
  // replace a live turn (and its Stop button) with an error page.
  if (!detail) {
    const status = (detailQuery.error as { status?: number } | null | undefined)?.status;
    // Only the backend's own "no such run" is a not-found; anything else is a
    // read we failed to make, and saying "not found" for it is a wrong answer.
    if (detailQuery.error && status !== 404) {
      return (
        <RunLoadFailed
          runId={runId}
          detail={
            detailQuery.error instanceof Error
              ? detailQuery.error.message
              : "The request failed."
          }
          backHref={backHref}
          onRetry={() => void detailQuery.refetch()}
          retrying={detailQuery.isFetching}
        />
      );
    }
    return <RunNotFound runId={runId} backHref={backHref} />;
  }
  // A bad run id in the URL must say so rather than fall back to the newest run.
  // A scheduled sweep carries the agent-scope sentinel and legitimately shows on
  // each project it worked, so only a DIFFERENT real project is the wrong one.
  if (
    detail.run.context_project_id &&
    detail.run.context_project_id !== projectId
  ) {
    return <RunNotFound runId={runId} backHref={backHref} />;
  }

  return (
    <section className={styles.runWithBar}>
      <div ref={contentRef} className={styles.runWithBarInner}>
        <header className={styles.detailHeader}>
          <div>
            <RunTitleBar run={detail.run} onDeleted={() => navigate(backHref)} />
            <p className={styles.muted}>
              Started by {runStarterLabel(detail.run)} ·{" "}
              {formatWhen(detail.run.created_at)}
              {runParticipantsLabel(detail.run) && (
                <> · with {runParticipantsLabel(detail.run)}</>
              )}
              {ended
                ? " · completed"
                : inFlight
                  ? " · the manager is working…"
                  : ""}
              <RunSpendText usd={detail.run.spend_usd} prefix=" · AI spend " />
            </p>
          </div>
          <div className={styles.detailHeaderActions}>
            {/* ONE trace for the whole conversation, keyed on the run - each turn
                appends to it, and it shares that key with the run's single
                ai_invocation row so /costs opens exactly this. A per-message button
                would open the same run-wide trace from every row. */}
            <ViewAiTraceButton
              surface="stateful_agent_runs"
              artefactId={detail.run.run_id}
              hasTrace={detail.messages.some((m) => m.has_ai_trace)}
              label="View AI trace"
            />
            <Link className={styles.link} to={backHref}>
              Back to runs
            </Link>
          </div>
        </header>

        <RunTranscript
          runId={detail.run.run_id}
          runKind={detail.run.kind}
          projectId={detail.run.context_project_id ?? null}
          seen
          messages={detail.messages}
          projects={[]}
          draftStatus={detail.draft_status}
          onReply={!ended && !detail.run.hidden_at ? quote : null}
          openResponses={false}
          openEmails={false}
        />

        <RunGeneratedFiles runId={detail.run.run_id} artifacts={detail.artifacts ?? []} />

        <RunDriveDocuments documents={detail.drive_documents ?? []} />

        {liveTurn && (
          <LiveTurnPanel
            turn={liveTurn}
            label={`${answeringAgent} is working…`}
            stopping={stopping}
            onStop={() => void onStop()}
            // Whose the untagged steps are. Read off the run rather than assumed to
            // be the Manager: a conversation started with a specialist answers as
            // that specialist, and calling it the Manager named the wrong agent on
            // every line of the feed.
            fallbackAgent={answeringAgent}
          />
        )}
        {error && <p className={styles.error}>{error}</p>}
      </div>

      {!ended && !detail?.run.hidden_at && (
        <div ref={barRef} className={styles.bottomBar}>
          <ScrollToLatest contentRef={contentRef} barRef={barRef} />
          <RunComposer
            value={message}
            onChange={setMessage}
            onSend={send}
            placeholder="Ask the manager something about this project…"
            disabled={false}
            queueing={inFlight}
            sending={turn.isLoading}
            textareaRef={composerRef}
            attachments={attachments}
          />
        </div>
      )}


    </section>
  );
};

/** This project's runs, as a table. Shared by the Scheduled and Conversations
 * tabs, which differ only in which kind they ask the server for and what they
 * say when the list is empty.
 */
/** This project's runs. A thin wrapper over the shared table: what a project
 * page adds is where a row LINKS (a scheduled pass belongs to the agent, even
 * though it worked this site) and the line explaining why a pass is listed here
 * at all. */
export const ProjectRunsTable = ({
  runs,
  projectId,
  projectName,
  showStatus = false,
  showType = false,
  showRunTime = false,
  allowDelete,
  agentLabel,
}: {
  runs: readonly StatefulRun[];
  projectId: string;
  projectName: string;
  showStatus?: boolean;
  showRunTime?: boolean;
  showType?: boolean;
  allowDelete: boolean;
  agentLabel?: (agent: string) => string;
}) => (
  <RunsTable
    runs={runs}
    allowDelete={allowDelete}
    showStatus={showStatus}
    showType={showType}
    showRunTime={showRunTime}
    agentLabel={agentLabel}
    // A sweep opens on this project's slice of it, which links to the whole run.
    href={(run) =>
      run.kind === "scheduled"
        ? projectScheduledPath(projectId, run.run_id)
        : runHref(projectId, run.run_id)
    }
    subLabel={(run) =>
      run.kind === "scheduled" ? `Processed ${projectName} on a scheduled pass` : null
    }
  />
);

// The deep link: /projects/:projectId/learning-agents/conversations/:runId, mirroring the
// sibling runs/:runId route so a run is linkable, refreshable and keeps the
// project hub's header + tab strip.
export const ProjectStatefulRunDetailRoute = () => {
  const { projectId, runId } = useParams<{ projectId: string; runId: string }>();
  const decodedProject = projectId ? decodeURIComponent(projectId) : "";
  const decodedRun = runId ? decodeURIComponent(runId) : "";
  const { user } = useKindeAuth();
  if (!decodedRun) {
    return (
      <RunNotFound
        runId=""
        backHref={runsHref(decodedProject)}
      />
    );
  }
  return (
    <RunDetailPanel
      // Keyed on the run: the panel holds per-run operator state, and a
      // `proposal_id` is only unique WITHIN a run ("<agent>:<rule_id>").
      key={decodedRun}
      runId={decodedRun}
      projectId={decodedProject}
      actorEmail={user?.email ?? ""}
    />
  );
};

export const ProjectStatefulRunsTab = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const { user } = useKindeAuth();
  const actorEmail = user?.email ?? "";
  const navigate = useNavigate();
  // A "Draft email" / "Ask the agent" link from a status tab lands here prefilled.
  const [searchParams] = useSearchParams();
  const [range, setRange] = useState(() => lastDays(RUNS_DEFAULT_DAYS));
  const [types, setTypes] = useState<string[]>([]);
  const runsQuery = useStatefulRuns(decoded, range, "conversations", types);
  const [error, setError] = useState<string | null>(null);
  const roster = useStatefulAgents().data?.agents ?? [];
  const start = useStartStatefulRun();
  const slot = useAgentSlot();

  const runs = useMemo(() => runsQuery.data?.runs ?? [], [runsQuery.data]);
  const projectName = runsQuery.data?.project_name ?? "this site";

  return (
    <div className={styles.page}>
      {/* No "you already have one open" gate: two writers on one state
          document are handled at commit by the moved-base check. */}
      <ConversationStarter
        roster={roster}
        initialMessage={searchParams.get("ask") ?? ""}
        initialAgent={searchParams.get("agent") ?? ""}
        isLoading={start.isLoading}
        error={error}
        placeholder="What do you want to ask the agents about this project?"
        onStart={(choice, started) => {
          setError(null);
          start.mutate(
            {
              project_id: decoded,
              config: slot,
              content: choice.content,
              actor_email: actorEmail,
              is_private: choice.isPrivate,
              ...choice.attachments,
              ...(choice.agent ? { agent: choice.agent } : {}),
              // Omitted rather than sent as the full list: absent is what the
              // server reads as unrestricted.
              ...(choice.specialists !== null
                ? { specialists: choice.specialists }
                : {}),
            },
            {
              onSuccess: (r) => {
                started();
                navigate(runHref(decoded, r.run_id));
              },
              onError: (e) => setError(e.message),
            },
          );
        }}
      />

      <h2 className={styles.sectionTitle}>Conversations</h2>
      <div className={styles.filters}>
        <DateRangePicker
          range={range}
          setRange={setRange}
          defaultDays={RUNS_DEFAULT_DAYS}
        />
        <TypeFilter selected={types} onChange={setTypes} />
      </div>
      {runsQuery.isLoading && <p className={styles.muted}>Loading…</p>}
      {runsQuery.isError && (
        <p className={styles.error}>Could not load this project&apos;s conversations.</p>
      )}
      {!runsQuery.isLoading && !runsQuery.isError && runs.length === 0 && (
        <p className={styles.muted}>
          No conversation about this project in this range.
        </p>
      )}
      {runs.length > 0 && (
        <ProjectRunsTable
          runs={runs}
          projectId={decoded}
          projectName={projectName}
          agentLabel={agentLabelFrom(roster)}
          showType
          allowDelete
        />
      )}
    </div>
  );
};
